// Editing a logged Clock-In session's times, and adding one after the fact
// (Jade, 2026-10-11, from a Toggl review: "I only need to log an activity and
// change its start or end whenever I forget"). Sessions live in
// clarity_focusSessions; these helpers keep both paths saving the same shape a
// real session has, so stats, the donut, the details panel and the widget
// need nothing extra.
//
// A session stays within one day (no crossing midnight) in this first version.

import { sameSession } from "./sessionGrouping";
import { addToLog, isAmountHabit, unitOf } from "./habitUnits";

const pad = (n) => String(n).padStart(2, "0");

/** "HH:MM" of an ISO instant, in local time ("" when missing). */
export function hhmmOf(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The local instant for "YYYY-MM-DD" + "HH:MM", as an ISO string. */
export function isoAt(dateStr, hhmm) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  const [h, mi] = String(hhmm).split(":").map(Number);
  return new Date(y, m - 1, d, h, mi, 0, 0).toISOString();
}

/** Whole minutes from start to end ("HH:MM" each, same day). */
export function minutesBetween(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return (eh * 60 + em) - (sh * 60 + sm);
}

/**
 * Why these times can't be saved, or null when they can. `now` is a Date.
 * Rules: both times set, end after start (same day), nothing in the future.
 */
export function sessionTimesError(dateStr, start, end, now = new Date()) {
  if (!dateStr) return "Pick a date";
  if (!start || !end) return "Set a start and end time";
  if (minutesBetween(start, end) < 1) return "End time must be after the start";
  if (new Date(isoAt(dateStr, end)).getTime() > now.getTime() + 60000) return "Can't be in the future";
  return null;
}

/**
 * "Just the length" (Jade, 2026-10-15): for a session whose start and end were
 * forgotten. Places `minutes` on the day so the rest of the app (which keeps a
 * start and an end on every session) needs nothing new:
 *   - today: it ends now (whole minute), so it can't run into the future;
 *   - an earlier day: centred on midday, kept inside the day.
 * Returns { start, end } ("HH:MM"), or { error } when it can't fit.
 */
export const DURATION_MAX_MINS = 23 * 60 + 59;
export function estimatedTimes(dateStr, minutes, now = new Date()) {
  const mins = Math.round(Number(minutes) || 0);
  if (!dateStr) return { error: "Pick a date" };
  if (mins < 1) return { error: "Set how long it was" };
  if (mins > DURATION_MAX_MINS) return { error: "A session stays within one day" };
  const hm = (t) => `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  if (dateStr > today) return { error: "Can't be in the future" };
  if (dateStr === today) {
    const endT = now.getHours() * 60 + now.getMinutes();
    if (endT - mins < 0) return { error: "Longer than today so far" };
    return { start: hm(endT - mins), end: hm(endT) };
  }
  const startT = Math.min(Math.max(0, 12 * 60 - Math.floor(mins / 2)), DURATION_MAX_MINS - mins);
  return { start: hm(startT), end: hm(startT + mins) };
}

/** "HH:MM" `mins` after `start`, or null when that would run past 23:59. */
export function endAfter(start, mins) {
  if (!start || !(mins >= 1)) return null;
  const [h, m] = start.split(":").map(Number);
  const t = h * 60 + m + Math.round(mins);
  if (t > DURATION_MAX_MINS) return null;
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
}

/**
 * The sessions array with `target`'s start/end moved (same day) and minutes recomputed.
 * `estimated` = the edit kept it duration-only (times placed for it, not picked);
 * otherwise real times were picked, so it's no longer a length-only session.
 */
export function withSessionTimes(sessions, target, start, end, estimated = false) {
  return sessions.map(s => {
    if (!sameSession(s, target)) return s;
    const next = { ...s, startedAt: isoAt(s.date, start), completedAt: isoAt(s.date, end), minutes: minutesBetween(start, end) };
    if (estimated) next.estimated = true; else delete next.estimated;
    return next;
  });
}

/** The sessions array without `target`. */
export function withoutSession(sessions, target) {
  return sessions.filter(s => !sameSession(s, target));
}

/**
 * A Time habit's logged minutes for a session's day follow its sessions: a
 * finished session adds its minutes there, so editing or deleting one moves
 * them by the difference. Returns the new habitLogs, or the same object when
 * the session isn't linked to a Time habit.
 */
export function habitLogsAfterSessionChange(habitLogs, habits, session, deltaMins) {
  if (!session || session.kind !== "habit" || session.refId == null || !deltaMins) return habitLogs;
  const h = (habits || []).find(x => x.id === session.refId);
  if (!h || !isAmountHabit(h) || unitOf(h) !== "time") return habitLogs;
  const day = session.date;
  const next = addToLog(h, habitLogs?.[day]?.[h.id], deltaMins);
  return { ...(habitLogs || {}), [day]: { ...((habitLogs || {})[day] || {}), [h.id]: next } };
}

/**
 * A new session for time that wasn't clocked live. `target` is what it was
 * for: { kind: "habit" | "task", ref } or { kind: "category", ref: category }
 * or null (nothing). Same fields a real session gets.
 */
export function pastSession({ target, date, start, end, note, categories = [], id, estimated = false }) {
  const minutes = minutesBetween(start, end);
  const base = { id, date, minutes, startedAt: isoAt(date, start), completedAt: isoAt(date, end) };
  // Logged by length only: the times were placed for it, not remembered.
  if (estimated) base.estimated = true;
  const text = String(note || "").trim();
  if (text) base.note = text;
  if (target && (target.kind === "habit" || target.kind === "task")) {
    const ref = target.ref || {};
    const cat = categories.find(c => c.id === ref.goalId);
    return {
      ...base,
      label: `${target.kind === "habit" ? "Habit" : "Task"}: ${ref.name || ref.title || ""}`,
      categoryLabel: cat ? cat.title : null, categoryId: ref.goalId || null,
      kind: target.kind, refId: ref.id,
    };
  }
  if (target && target.kind === "category") {
    return { ...base, label: target.ref.title, categoryLabel: target.ref.title, categoryId: target.ref.id };
  }
  return { ...base, label: null, categoryLabel: null, categoryId: null };
}
